import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

const DRAG_THRESHOLD = 6;

/**
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLButtonElement[]} [items]
 *
 * @extends {Component<Refs>}
 */
export class ProductMediaThumbnailNav extends Component {
  requiredRefs = ['viewport', 'previous', 'next'];

  /** @type {AbortController | null} */
  #controller = null;
  #dragStartX = 0;
  #dragStartScrollLeft = 0;
  #dragging = false;
  #suppressClick = false;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    this.refs.viewport.addEventListener('scroll', this.#updateArrowState, { signal, passive: true });
    this.refs.viewport.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    window.addEventListener('resize', this.#updateArrowState, { signal });

    queueMicrotask(() => {
      this.#updateArrowState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  get slideshow() {
    const slideshow = this.closest('slideshow-component');
    return slideshow instanceof HTMLElement ? slideshow : null;
  }

  handleThumbnailClick(index, event) {
    if (this.#suppressClick) {
      event?.preventDefault();
      return;
    }

    this.slideshow?.select?.(index, event);
  }

  previous(event) {
    event?.preventDefault();
    this.#scrollRail(-1);
  }

  next(event) {
    event?.preventDefault();
    this.#scrollRail(1);
  }

  #handleSlideshowSelect = (event) => {
    this.#syncSelectedState(event.detail.index);
  };

  #syncSelectedState(index) {
    this.refs.items?.forEach((item, itemIndex) => {
      item.setAttribute('aria-selected', `${itemIndex === index}`);
    });
  }

  #scrollRail(direction) {
    const { viewport } = this.refs;
    const step = Math.max(viewport.clientWidth, 1);

    viewport.scrollBy({
      left: direction * step,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #updateArrowState = () => {
    const { viewport, previous, next } = this.refs;
    const maxScrollLeft = Math.max(viewport.scrollWidth - viewport.clientWidth, 0);
    const threshold = 2;

    previous.disabled = viewport.scrollLeft <= threshold;
    next.disabled = viewport.scrollLeft >= maxScrollLeft - threshold;
  };

  #handlePointerDown = (event) => {
    const { viewport } = this.refs;

    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.refs.items?.length === 0) return;

    this.#dragStartX = event.clientX;
    this.#dragStartScrollLeft = viewport.scrollLeft;
    this.#dragging = false;

    const onPointerMove = (moveEvent) => this.#handlePointerMove(moveEvent);
    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      if (!this.#dragging) return;

      this.#dragging = false;
      this.removeAttribute('dragging');
      this.#suppressClick = true;

      window.setTimeout(() => {
        this.#suppressClick = false;
      }, 0);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  };

  #handlePointerMove = (event) => {
    const { viewport } = this.refs;
    const delta = event.clientX - this.#dragStartX;

    if (!this.#dragging && Math.abs(delta) < DRAG_THRESHOLD) return;

    if (!this.#dragging) {
      this.#dragging = true;
      this.setAttribute('dragging', '');
    }

    event.preventDefault();
    viewport.scrollLeft = this.#dragStartScrollLeft - delta;
  };
}

if (!customElements.get('product-media-thumbnail-nav')) {
  customElements.define('product-media-thumbnail-nav', ProductMediaThumbnailNav);
}
